import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { Plug, Check, X, Copy } from 'lucide-react';
import { getConnections, connectProvider, disconnectProvider } from '../utils/api';

const PROVIDERS_META = {
  servicetitan: {
    color: 'border-lime/40',
    blurb: 'The leading FSM platform for HVAC, Plumbing & Electrical. Job & booking events sync automatically.',
  },
  jobber: {
    color: 'border-blue-500/40',
    blurb: 'Popular with growing home service teams. Job and invoice events sync automatically.',
  },
  housecallpro: {
    color: 'border-orange/40',
    blurb: 'All-in-one tool for smaller crews. Job events sync automatically.',
  },
};

export default function Integrations() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await getConnections();
      setData(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const handleConnect = async (provider) => {
    setBusy(provider);
    try {
      const res = await connectProvider(provider);
      if (res.data.sandbox) {
        flash(`${provider} connected in sandbox mode — real OAuth kicks in once API credentials are added`);
        fetchData();
      } else {
        window.location.href = res.data.authorize_url;
      }
    } catch (e) {
      flash(e.response?.data?.detail || 'Connection failed');
    } finally { setBusy(''); }
  };

  const handleDisconnect = async (provider) => {
    setBusy(provider);
    try {
      await disconnectProvider(provider);
      fetchData();
    } catch (e) {
      flash(e.response?.data?.detail || 'Disconnect failed');
    } finally { setBusy(''); }
  };

  if (!data) return <Layout variant="light"><div className="text-gray py-16 text-center">Loading…</div></Layout>;

  const connByProvider = Object.fromEntries((data.connections || []).map((c) => [c.provider, c]));
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <Layout variant="light">
      <div data-testid="integrations-page">
        <h1 className="text-3xl font-black text-white flex items-center gap-3 mb-2">
          <Plug className="text-lime" size={28} /> Booking System Integrations
        </h1>
        <p className="text-gray mb-8">
          Connect your field service software and campaign bookings are tracked automatically — no manual entry.
        </p>
        {msg && <div className="mb-6 p-3 rounded-lg bg-lime/10 border border-lime/40 text-lime text-sm" data-testid="integrations-message">{msg}</div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(data.providers || []).map((p) => {
            const conn = connByProvider[p.provider];
            const connected = conn?.status === 'connected';
            const meta = PROVIDERS_META[p.provider] || {};
            return (
              <div key={p.provider} data-testid={`provider-card-${p.provider}`} className={`bg-card-bg border ${connected ? meta.color : 'border-divider'} rounded-xl p-6 flex flex-col`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-bold text-xl">{p.display_name}</h3>
                  {connected ? (
                    <span data-testid={`provider-status-${p.provider}`} className="px-2.5 py-1 rounded-full text-xs font-bold bg-lime/15 text-lime border border-lime/40 flex items-center gap-1">
                      <Check size={12} /> Connected{conn.sandbox ? ' (Sandbox)' : ''}
                    </span>
                  ) : (
                    <span data-testid={`provider-status-${p.provider}`} className="px-2.5 py-1 rounded-full text-xs font-medium bg-white/5 text-gray border border-divider">
                      Not connected
                    </span>
                  )}
                </div>
                <p className="text-light-gray text-sm mb-4 flex-1">{meta.blurb}</p>
                {!p.live_configured && (
                  <p className="text-xs text-orange mb-4">Sandbox mode — connects instantly with test credentials until live API keys are added.</p>
                )}
                <div className="mb-4">
                  <p className="text-xs text-gray uppercase font-semibold mb-1">Webhook URL</p>
                  <div className="flex items-center gap-2 bg-black rounded-lg border border-divider px-3 py-2">
                    <code className="text-xs text-gray-light truncate flex-1">{backendUrl}/api/webhooks/{p.provider}</code>
                    <button
                      data-testid={`copy-webhook-${p.provider}`}
                      onClick={() => navigator.clipboard.writeText(`${backendUrl}/api/webhooks/${p.provider}`)}
                      className="text-gray hover:text-white shrink-0"
                    >
                      <Copy size={13} />
                    </button>
                  </div>
                </div>
                {connected ? (
                  <button
                    data-testid={`disconnect-btn-${p.provider}`}
                    onClick={() => handleDisconnect(p.provider)}
                    disabled={busy === p.provider}
                    className="w-full px-4 py-2.5 rounded-lg border border-orange/40 text-orange text-sm font-bold hover:bg-orange/10 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <X size={14} /> Disconnect
                  </button>
                ) : (
                  <button
                    data-testid={`connect-btn-${p.provider}`}
                    onClick={() => handleConnect(p.provider)}
                    disabled={busy === p.provider}
                    className="w-full px-4 py-2.5 rounded-lg bg-lime text-black text-sm font-bold hover:bg-lime/90 disabled:opacity-50"
                  >
                    {busy === p.provider ? 'Connecting…' : `Connect ${p.display_name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8 p-5 bg-card-bg border border-divider rounded-xl">
          <h3 className="text-white font-bold text-xl mb-2">How auto-tracking works</h3>
          <ol className="text-sm text-light-gray space-y-1.5 list-decimal list-inside">
            <li>Deploy a campaign — you get a unique tracking code (e.g. <code className="text-orange">GC-HVAC-NOCOOL-9696</code>)</li>
            <li>Use the code in your ad UTMs, booking form notes, or job custom fields</li>
            <li>When a job is created in your booking system, the webhook fires here</li>
            <li>We match the code and increment that campaign's booked-jobs count automatically</li>
          </ol>
        </div>
      </div>
    </Layout>
  );
}
